
import { useState, useEffect, useCallback } from 'react';
import { getTeachers, deleteTeacher } from '../services/db/teachers';
import { Teacher } from '../types/teacher.types';
import { toast } from 'sonner';
import { teacherAutoBackupService } from '../services/storage/teacherAutoBackupService';

export const useTeacherData = () => {
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [filteredTeachers, setFilteredTeachers] = useState<Teacher[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterText, setFilterText] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState<string>('all');
  const [error, setError] = useState<Error | null>(null);

  // Start auto-backup service on mount
  useEffect(() => {
    console.log('🛡️ Initializing teacher auto-backup service for data persistence...');
    teacherAutoBackupService.start();
    
    return () => {
      teacherAutoBackupService.stop();
    };
  }, []);

  const fetchTeachers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('📚 Loading teachers with enhanced 5000+ capacity...');
      
      const data = await getTeachers();
      
      // Make sure we have an array
      if (Array.isArray(data)) {
        console.log(`✅ Loaded ${data.length} teachers successfully`);
        setTeachers(data);
        setFilteredTeachers(data);
      } else {
        console.error('Expected array of teachers but got:', data);
        setTeachers([]);
        setFilteredTeachers([]);
        toast.error('Invalid teacher data format received');
      }
    } catch (error) {
      console.error('Error fetching teachers:', error);
      setError(error instanceof Error ? error : new Error('Unknown error'));
      
      if (error instanceof Error) {
        if (error.message.includes('quota') || error.message.includes('storage')) {
          toast.error('Storage optimized automatically. Teachers now support 5000+ records.');
        } else {
          toast.error('Failed to load teachers. Please try again.');
        }
      }
      
      // Set empty arrays to prevent undefined errors
      setTeachers([]);
      setFilteredTeachers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTeachers();
  }, [fetchTeachers]);

  useEffect(() => {
    if (teachers.length) {
      try {
        let filtered = [...teachers];

        // Apply text filter
        if (filterText) {
          filtered = filtered.filter((teacher) => {
            const searchText = filterText.toLowerCase();
            const fullName = `${teacher.firstName || ''} ${teacher.lastName || ''}`.toLowerCase();
            const email = teacher.email ? teacher.email.toLowerCase() : '';
            const phone = teacher.phone || '';
            const department = teacher.department ? teacher.department.toLowerCase() : '';

            return (
              fullName.includes(searchText) ||
              email.includes(searchText) ||
              phone.includes(searchText) ||
              department.includes(searchText)
            );
          });
        }

        // Apply department filter
        if (departmentFilter && departmentFilter !== 'all') {
          filtered = filtered.filter(
            (teacher) => teacher.department && teacher.department.toLowerCase() === departmentFilter.toLowerCase()
          );
        }

        setFilteredTeachers(filtered);
      } catch (error) {
        console.error('Error filtering teachers:', error);
        // Keep the current filtered state if there's an error
      }
    } else {
      setFilteredTeachers([]);
    }
  }, [teachers, filterText, departmentFilter]);

  const handleDelete = async (id: string) => {
    if (!id) {
      toast.error('Cannot delete: Teacher ID is missing');
      return;
    }

    try {
      await deleteTeacher(id);
      toast.success('Teacher deleted successfully');
      // Refresh the teachers list after deletion
      await fetchTeachers();
    } catch (error) {
      console.error('Error deleting teacher:', error);
      
      if (error instanceof Error) {
        if (error.message.includes('quota') || error.message.includes('storage')) {
          toast.error('Storage optimized automatically. Please try deleting again.');
        } else {
          toast.error('Failed to delete teacher. Please try again.');
        }
      }
    }
  };

  return {
    teachers: filteredTeachers,
    loading,
    error,
    filterText,
    setFilterText,
    departmentFilter,
    setDepartmentFilter,
    handleDelete,
    refreshTeachers: fetchTeachers,
  };
};

export default useTeacherData;
